import {
  UsergroupAddOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Modal, Input, Select, Tooltip } from 'antd';
import { Form } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../../baseComponents';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import { FronteggContext } from '@frontegg/rest-api';
import { Axios } from '../../../lib';
import { getFronteggRolesCurrentOrg } from 'src/modules/SuperAdmin/store/actions/practitioner.action';
import { SuperAdminTable as ConditionsTable } from 'src/modules/SuperAdmin/components/baseComponents/Table';
import { useGetPractitionerList } from 'src/hooks/practitioner';
import { useAuth } from '@frontegg/react';
import { analytics } from 'src/services';
import { useGetPractitionerDetails } from 'src/hooks/practitioner';
import { useGetSpecialityList } from 'src/hooks/speciality';
import { useFeatureEntitlements } from '@frontegg/react';

const getPublicRoles = (fronteggRoles, isPhoneTriageModuleEntitled) => {
  const publicRoles = ['admin', 'user', 'clinician', 'medical-assistant'];
  if (isPhoneTriageModuleEntitled) {
    publicRoles.push('phone-agent');
  }

  return (fronteggRoles || [])?.filter((role) =>
    publicRoles.includes((role?.key || '').toLowerCase().trim())
  );
};

const conditionsTableColumns = [
  {
    title: <span className="font-[700]"> Condition name</span>,
    dataIndex: 'condition_name',
    key: 'condition_name',
    width: '10%',
  },
  {
    title: <span className="font-[700]"> Schedule</span>,
    dataIndex: 'schedule',
    key: 'schedule',
    width: '20%',
  },
];

export function InvitePractitioner({ mobile, isMinimized }) {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const { fronteggRolesCurrentOrg } = useSelector(
    (state) => state?.doctorsState
  );

  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );

  const { data: practitionerList, isLoading: isPractitionerListLoading } =
    useGetPractitionerList();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();
  const mirrorUserId = Form.useWatch(['mirrorUserId'], form);
  const { isEntitled: isPhoneTriageModuleEntitled } =
    useFeatureEntitlements('phone-triage');

  const {
    data: mirrorPractitionerDetails,
    isLoading: isMirrorPractitionerDetailsLoading,
  } = useGetPractitionerDetails(
    {
      practitionerId: mirrorUserId,
    },
    {
      enabled: !!mirrorUserId,
    }
  );

  const { data: specialties, isLoading: isSpecialtiesLoading } =
    useGetSpecialityList();

  const practitionerOptions = useMemo(() => {
    return (practitionerList?.practitioners || [])?.map((practitioner) => ({
      value: practitioner.practitioner_id,
      label: practitioner.full_name,
      conditions: practitioner.conditions.map((condition) => ({
        ...condition,
        notification_preference:
          condition.notification_preferences.flat() || [],
      })),
    }));
  }, [practitionerList?.practitioners]);

  const fronteggRoleOptions = useMemo(() => {
    return (
      getPublicRoles(fronteggRolesCurrentOrg, isPhoneTriageModuleEntitled) || []
    )?.map((role) => ({
      value: role?.name,
      label: role?.name,
      id: role?.id,
    }));
  }, [fronteggRolesCurrentOrg, isPhoneTriageModuleEntitled]);

  const specialtyOptions = useMemo(() => {
    return (specialties?.specialties || []).map((specialty, index) => ({
      key: `${specialty.specialty_id}_${index}`,
      value: specialty.specialty_id,
      label: specialty.specialty_name ? specialty.specialty_name : 'N/A',
    }));
  }, [specialties?.specialties]);

  const closeModal = () => {
    form.resetFields();
    setIsModalOpen(false);
  };

  const openModal = () => {
    // eslint-disable-next-line no-undef
    analytics.track('Clicked Invite User For VCA', {
      mobile,
      email: user?.email,
    });
    setIsModalOpen(true);
  };

  const cancelModal = () => {
    // eslint-disable-next-line no-undef
    analytics.track('Clicked Cancel In Invite User for VCA', {
      mobile,
      email: user?.email,
    });
    closeModal();
  };

  useEffect(() => {
    dispatch(getFronteggRolesCurrentOrg(currentPractitioner?.org_uuid));
  }, [currentPractitioner?.org_uuid, dispatch]);

  useEffect(
    () => {
      if (mirrorPractitionerDetails) {
        const matchingSpecialityOption = specialtyOptions.find(
          (option) =>
            option.value ===
            mirrorPractitionerDetails?.specialty_details?.specialty_id
        );

        form.setFieldsValue({
          specialty: matchingSpecialityOption?.value || undefined,
        });

        let matchingFronteggRoleOption = undefined;
        (mirrorPractitionerDetails?.frontegg_roles || []).some((roleId) => {
          matchingFronteggRoleOption = fronteggRoleOptions.find(
            (option) => option.id === roleId
          );
          return !!matchingFronteggRoleOption;
        });

        form.setFieldsValue({
          role: matchingFronteggRoleOption?.value || undefined,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      mirrorPractitionerDetails?.practitioner_details?.practitioner_id,
      specialtyOptions.length,
      fronteggRoleOptions.length,
    ]
  );

  const inviteUser = async () => {
    const values = await form.validateFields();

    setIsSubmitting(true);
    const { mirrorUserId, role, specialty, firstName, lastName, email } =
      values;

    const token = FronteggContext.getAccessToken();
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    };

    try {
      const { condition_details, role_details } =
        mirrorPractitionerDetails || {};

      const payload = {
        org_uuid: currentPractitioner?.org_uuid,
        mirror_practitioner_id: mirrorUserId,
        conditions: condition_details?.map(
          (condition) => condition.condition_id
        ),
        primary_specialty_id: specialty,
        frontegg_role: role,
        role_id: role_details?.role_id,
        first_name: (firstName || '').trim(),
        last_name: (lastName || '').trim(),
        create_frontegg_user: true,
        email: {
          email_text: (email || '').trim(),
          email_type: '',
        },
      };

      await Axios.post(`/practitioner`, payload, { headers });
      showToastSuccess('User invited successfully');
      closeModal();
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || 'Failed to invite user';
      showToastError(errorMessage);
    }

    setIsSubmitting(false);
    // eslint-disable-next-line no-undef
    analytics.track('Created Submit In Invite User for VCA', {
      email,
      mobile,
    });
  };

  function renderInviteModal() {
    return (
      <Modal
        centered
        closable={isSubmitting}
        title="Invite User"
        open={isModalOpen}
        onCancel={cancelModal}
        footer={
          <div className="flex justify-end gap-2 mt-8">
            <Button
              disabled={isSubmitting}
              className="ih-button-secondary"
              onClick={cancelModal}
            >
              Cancel
            </Button>
            <Button
              isLoading={isSubmitting}
              type="submit"
              className="ih-button-primary"
              onClick={inviteUser}
            >
              Invite
            </Button>
          </div>
        }
      >
        <Form
          form={form}
          layout="vertical"
          className="space-y-4"
          autoComplete="off"
        >
          <div className="hidden" />
          <Form.Item
            label="Mirror User"
            className="field-wrapper"
            tooltip="Clone your roles and permissions to grant identical profile to the new user"
            name="mirrorUserId"
          >
            <Select
              disabled={isPractitionerListLoading}
              prefix={isPractitionerListLoading ? <LoadingOutlined /> : null}
              showSearch
              allowClear
              optionFilterProp="label"
              className="field"
              placeholder="Search user"
            >
              {practitionerOptions.map((option) => (
                <Select.Option
                  key={option.key}
                  value={option.value}
                  label={option.label}
                >
                  <div className="flex items-center gap-2">
                    <span>{option.label}</span>
                    <Tooltip
                      placement="right"
                      title={
                        <div className="w-[40rem]">
                          <div className="ant-table overflow-auto">
                            <ConditionsTable
                              className="appointment-page-table"
                              columns={conditionsTableColumns}
                              data={(option.conditions || []).map(
                                (condition) => ({
                                  condition_name: condition.condition_name,
                                  schedule: condition.notification_preference
                                    .map(
                                      (pref) =>
                                        `${pref.number_of} ${pref.duration}`
                                    )
                                    .join(', '),
                                })
                              )}
                              loading={false}
                            />
                          </div>
                        </div>
                      }
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Primary Specialty"
            name="specialty"
            className="field-wrapper"
            rules={[{ required: true, message: 'Specialty is required' }]}
          >
            <Select
              disabled={
                isMirrorPractitionerDetailsLoading || isSpecialtiesLoading
              }
              prefix={
                isMirrorPractitionerDetailsLoading || isSpecialtiesLoading ? (
                  <LoadingOutlined />
                ) : null
              }
              showSearch
              optionFilterProp="label"
              className="field"
              placeholder="Search specialty"
              options={specialtyOptions}
            />
          </Form.Item>
          <Form.Item
            label="Role"
            name="role"
            className="field-wrapper"
            rules={[{ required: true, message: 'Role is required' }]}
          >
            <Select
              disabled={isMirrorPractitionerDetailsLoading}
              prefix={
                isMirrorPractitionerDetailsLoading ? <LoadingOutlined /> : null
              }
              optionFilterProp="label"
              className="field"
              placeholder="Search role"
              options={fronteggRoleOptions}
            />
          </Form.Item>
          <Form.Item
            label="First name"
            name="firstName"
            className="field-wrapper"
            rules={[
              { required: true, message: 'First name is required' },
              { whitespace: true, message: 'First name cannot be empty space' },
            ]}
          >
            <Input className="field" placeholder="Enter first name" />
          </Form.Item>
          <Form.Item
            label="Last name"
            name="lastName"
            className="field-wrapper"
            rules={[
              { required: true, message: 'Last name is required' },
              { whitespace: true, message: 'Last name cannot be empty space' },
            ]}
          >
            <Input className="field" placeholder="Enter last name" />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            className="field-wrapper"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Enter a valid email' },
            ]}
          >
            <Input className="field" type="email" placeholder="Enter email" />
          </Form.Item>
        </Form>
      </Modal>
    );
  }

  const lgButton = (
    <button
      className={`w-full text-white bg-gradient-to-br from-pink-500 to-blue-700 bg-origin-border bg-clip-padding flex justify-center items-center p-2 rounded-xl hover:shadow-xl font-medium px-4 gap-2`}
      type="button"
      onClick={openModal}
    >
      <UsergroupAddOutlined />
      Invite Your Team
    </button>
  );

  const mobileButton = (
    <button
      className={`text-white text-lg bg-gradient-to-br from-pink-500 to-blue-700 bg-origin-border bg-clip-padding flex items-center p-3 rounded-full shadow-xl`}
      type="button"
      onClick={openModal}
    >
      <UsergroupAddOutlined />
    </button>
  );

  const minimizedButton = (
    <button
      className="text-white text-lg bg-gradient-to-br from-pink-500 to-blue-700 bg-origin-border bg-clip-padding flex items-center p-3 rounded-full shadow-xl"
      onClick={openModal}
    >
      <UsergroupAddOutlined />
    </button>
  );

  return (
    <>
      {mobile ? mobileButton : isMinimized ? minimizedButton : lgButton}
      {isModalOpen && renderInviteModal()}
    </>
  );
}
